import React, { useState } from "react";
import { OfferFilterV2 } from "../../../../types/Transaction";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import { HStack, VStack } from "../../../../components/utils";
import { Button, ChevronDown, Search, X } from "./ComponentUtils";
import { Input } from "../../../../components/shared/InputField";
import { FilterType } from "./FilterOptions";
import { Select } from "../../../../components/shared/Select";

export function NumberFilterPopOver({
  index,
  filter,
  formik,
  disableUpdate = false,
}: {
  index: number;
  filter: FilterType;
  formik: any;
  disableUpdate: boolean;
}) {
  const isFilterActive = (filterId: string, filterType: string): boolean => {
    const filterValue =
      formik.values.offerFilter[filterId as keyof OfferFilterV2];

    if (filterType === "number") {
      const { min, max } =
        (filterValue as { min?: number; max?: number }) || {};
      return (min !== undefined && min > 0) || (max !== undefined && max > 0);
    }

    return false;
  };
  const [tempFilter, setTempFilter] = useState<{
    type: "Above" | "Below" | "Between";
    min?: number;
    max?: number;
  }>(() => {
    const filterValue = formik.values.offerFilter[
      filter.id as keyof OfferFilterV2
    ] as { min?: number; max?: number } | undefined;

    if (filterValue) {
      // Determine the filter type based on the values in filterState
      if (filterValue.min !== undefined && filterValue.max !== undefined) {
        return {
          type: "Between",
          min: filterValue.min,
          max: filterValue.max,
        };
      } else if (filterValue.min !== undefined) {
        return { type: "Above", min: filterValue.min };
      } else if (filterValue.max !== undefined) {
        return { type: "Below", max: filterValue.max };
      }
    }

    return {
      type: "Above",
      min: 0,
      max: Infinity,
    };
  });

  return (
    <Popover
      key={index}
      content={
        <PopoverContent className="w-60">
          <VStack className="gap-3">
            {/* Filter Type Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Filter Type
              </label>
              <Select
                className="w-full mt-1 border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500"
                value={tempFilter.type}
                onChange={(e) =>
                  setTempFilter((prev) => ({
                    ...prev,
                    type: e.target.value as "Above" | "Below" | "Between",
                    max: e.target.value === "Between" ? prev.max : undefined,
                  }))
                }
                options={[
                  { value: "Above", label: "Above" },
                  { value: "Below", label: "Below" },
                  { value: "Between", label: "Between" },
                ]}
                textGetter={(option) => option.label}
                valueGetter={(option) => option.value}
              ></Select>
            </div>

            {/* Number Inputs */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {tempFilter.type === "Between" ? "Min" : "Value"}
              </label>
              <Input
                type="number"
                className="w-full mt-1"
                value={
                  tempFilter.type === "Below"
                    ? tempFilter.max || ""
                    : tempFilter.min || ""
                }
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10) || undefined;
                  setTempFilter((prev) => ({
                    ...prev,
                    ...(tempFilter.type === "Below"
                      ? { max: value }
                      : { min: value }),
                  }));
                }}
              />
            </div>

            {tempFilter.type === "Between" && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Max
                </label>
                <Input
                  type="number"
                  className="w-full mt-1"
                  value={tempFilter.max || ""}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10) || undefined;
                    setTempFilter((prev) => ({
                      ...prev,
                      max: value,
                    }));
                  }}
                />
              </div>
            )}

            {/* Clear and Apply Buttons */}
            {!disableUpdate && (
              <HStack className="justify-between pt-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setTempFilter({
                      type: "Above",
                      min: undefined,
                      max: undefined,
                    });
                    formik.setFieldValue(`offerFilter.${filter.id}`, {});
                  }}
                >
                  Clear
                </Button>
                <Button
                  className="text-white bg-orange-500 hover:bg-orange-600"
                  variant="primary"
                  onClick={() => {
                    formik.setFieldValue(`offerFilter.${filter.id}`, {
                      min:
                        tempFilter.type === "Below"
                          ? undefined
                          : tempFilter.min,
                      max:
                        tempFilter.type === "Above"
                          ? undefined
                          : tempFilter.max,
                    });
                  }}
                >
                  Apply
                </Button>
              </HStack>
            )}
          </VStack>
        </PopoverContent>
      }
    >
      <PopoverTrigger>
        <Button
          variant="outline"
          className={`flex items-center gap-2 ${
            isFilterActive(filter.id, filter.type)
              ? "bg-[#003366] text-white hover:bg-[#003366] !important"
              : ""
          }`}
        >
          {filter.label}
          {isFilterActive(filter.id, filter.type) && (
            <span>
              {(
                formik.values.offerFilter[filter.id as keyof OfferFilterV2] as {
                  min: number;
                  max?: number;
                }
              )?.min !== undefined &&
              (
                formik.values.offerFilter[filter.id as keyof OfferFilterV2] as {
                  min: number;
                  max?: number;
                }
              )?.max !== undefined
                ? // If both min and max are present, show "Between" range
                  `> ${
                    (
                      formik.values.offerFilter[
                        filter.id as keyof OfferFilterV2
                      ] as {
                        min: number;
                        max?: number;
                      }
                    )?.min
                  } & < ${
                    (
                      formik.values.offerFilter[
                        filter.id as keyof OfferFilterV2
                      ] as {
                        min: number;
                        max?: number;
                      }
                    )?.max
                  }`
                : // If only min is present
                (
                    formik.values.offerFilter[
                      filter.id as keyof OfferFilterV2
                    ] as {
                      min: number;
                      max?: number;
                    }
                  )?.min !== undefined
                ? `> ${
                    (
                      formik.values.offerFilter[
                        filter.id as keyof OfferFilterV2
                      ] as {
                        min: number;
                        max?: number;
                      }
                    )?.min
                  }`
                : // If only max is present
                  ` < ${
                    (
                      formik.values.offerFilter[
                        filter.id as keyof OfferFilterV2
                      ] as {
                        min: number;
                        max?: number;
                      }
                    )?.max
                  }`}
            </span>
          )}
          {isFilterActive(filter.id, filter.type) && !disableUpdate ? (
            <X
              className="w-4 h-4 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setTempFilter({
                  type: "Above",
                  min: undefined,
                  max: undefined,
                });
                formik.setFieldValue(`offerFilter.${filter.id}`, {});
              }}
            />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </Button>
      </PopoverTrigger>
    </Popover>
  );
}
